/**
 * UTC occupancy calendar helpers for the unit short-term stays tab.
 * Overlap math is half-open UTC windows, matching occupancy.ts.
 * Manager-facing dates/times use STAY_DISPLAY_TIMEZONE (see stayDisplayTimezone.ts).
 */

import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { STAY_DISPLAY_TIMEZONE, stayDisplayLocale } from "./stayDisplayTimezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export type StayCalendarKind =
  | "lease"
  | "stay"
  | "pending"
  | "confirmed"
  | "checkedIn"
  | "completed"
  | "block";

export type StayCalendarEvent = {
  kind: StayCalendarKind;
  id: string;
  label: string;
  start: string;
  end: string;
  href?: string;
  status?: string;
};

export type StayCalendarDay = {
  /** YYYY-MM-DD in the display timezone */
  date: string;
  inMonth: boolean;
  isToday: boolean;
  events: StayCalendarEvent[];
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const BLOCKING_LEASE_STATUSES = new Set(["Active", "Terminating"]);
export const CONFIRMED_STAY_STATUSES = new Set(["Confirmed", "CheckedIn"]);
export const PENDING_STAY_STATUS = "Pending";
export const STAY_CALENDAR_OCCUPANCY_KINDS = new Set<StayCalendarKind>([
  "stay",
  "confirmed",
  "checkedIn",
  "completed",
]);

export function calendarKindFromStayStatus(
  status: string,
): StayCalendarKind | null {
  switch (status) {
    case "Pending":
      return "pending";
    case "Confirmed":
      return "confirmed";
    case "CheckedIn":
      return "checkedIn";
    case "Completed":
      return "completed";
    default:
      return null;
  }
}

export function matchesStayCalendarKindFilter(
  event: Pick<StayCalendarEvent, "kind">,
  filter: "all" | StayCalendarKind,
): boolean {
  if (filter === "all") return true;
  if (filter === "stay") {
    return STAY_CALENDAR_OCCUPANCY_KINDS.has(event.kind);
  }
  return event.kind === filter;
}

export function utcCalendarDate(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayUtcDate(): string {
  return utcCalendarDate(Date.now());
}

export function parseUtcInstant(iso: string): number | null {
  const ms = Date.parse(iso.trim());
  return Number.isNaN(ms) ? null : ms;
}

/** Inclusive UTC calendar day start, accepting ISO or YYYY/MM/DD. */
export function utcDayStartMs(raw: string): number | null {
  const trimmed = raw.trim();
  const slash = trimmed.match(/^(\d{4})[/-](\d{2})[/-](\d{2})/);
  if (slash && !trimmed.includes("T")) {
    return Date.UTC(Number(slash[1]), Number(slash[2]) - 1, Number(slash[3]));
  }
  const ms = parseUtcInstant(trimmed);
  if (ms == null) return null;
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function utcDayWindow(date: string): { startMs: number; endMs: number } | null {
  const startMs = utcDayStartMs(date);
  if (startMs == null) return null;
  return { startMs, endMs: startMs + MS_PER_DAY };
}

export function windowsOverlap(
  a: { startMs: number; endMs: number },
  b: { startMs: number; endMs: number },
): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

export function stayWindow(checkIn: string, checkOut: string): {
  startMs: number;
  endMs: number;
} | null {
  const startMs = parseUtcInstant(checkIn);
  const endMs = parseUtcInstant(checkOut);
  if (startMs == null || endMs == null || !(endMs > startMs)) return null;
  return { startMs, endMs };
}

export function leaseWindow(input: {
  startDate?: string | null;
  endDate?: string | null;
  occupancyEnd?: string | null;
}): { startMs: number; endMs: number } | null {
  if (!input.startDate?.trim()) return null;
  const startMs = utcDayStartMs(input.startDate);
  if (startMs == null) return null;
  const endRaw = input.occupancyEnd?.trim() || input.endDate?.trim();
  if (!endRaw) return { startMs, endMs: Number.POSITIVE_INFINITY };
  const endDay = utcDayStartMs(endRaw);
  if (endDay == null) return null;
  const endMs = endDay + MS_PER_DAY;
  if (!(endMs > startMs)) return null;
  return { startMs, endMs };
}

export function eventOverlapsUtcDay(
  event: Pick<StayCalendarEvent, "start" | "end" | "kind">,
  utcDate: string,
): boolean {
  const day = utcDayWindow(utcDate);
  if (!day) return false;
  const window =
    event.kind === "lease"
      ? leaseWindow({ startDate: event.start, endDate: event.end })
      : stayWindow(event.start, event.end);
  if (!window) return false;
  return windowsOverlap(window, day);
}

export function stayNights(checkIn: string, checkOut: string): number {
  const startMs = parseUtcInstant(checkIn);
  const endMs = parseUtcInstant(checkOut);
  if (startMs == null || endMs == null || !(endMs > startMs)) return 0;
  const start = new Date(startMs);
  const end = new Date(endMs);
  const startDay = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate(),
  );
  const endDay = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate(),
  );
  return Math.max(0, Math.round((endDay - startDay) / MS_PER_DAY));
}

/** Sunday-start month grid in UTC (6 weeks). */
export function buildUtcMonthGrid(
  year: number,
  monthIndex: number,
  events: StayCalendarEvent[],
  nowMs: number = Date.now(),
): StayCalendarDay[] {
  const first = Date.UTC(year, monthIndex, 1);
  const startPad = new Date(first).getUTCDay();
  const gridStart = first - startPad * MS_PER_DAY;
  const today = utcCalendarDate(nowMs);
  const days: StayCalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const ms = gridStart + i * MS_PER_DAY;
    const date = utcCalendarDate(ms);
    const inMonth = new Date(ms).getUTCMonth() === monthIndex;
    days.push({
      date,
      inMonth,
      isToday: date === today,
      events: events.filter((event) => eventOverlapsUtcDay(event, date)),
    });
  }
  return days;
}

/**
 * Sunday-start month grid in the stay display timezone (Cameroon / Douala for now).
 * Day coloring still uses UTC occupancy instants against that local day's window.
 */
export function buildDisplayMonthGrid(
  year: number,
  monthIndex: number,
  events: StayCalendarEvent[],
  nowMs: number = Date.now(),
  timeZone: string = STAY_DISPLAY_TIMEZONE,
): StayCalendarDay[] {
  const monthStart = dayjs.tz(
    `${year}-${String(monthIndex + 1).padStart(2, "0")}-01 00:00:00`,
    timeZone,
  );
  const gridStart = monthStart.subtract(monthStart.day(), "day");
  const today = dayjs(nowMs).tz(timeZone).format("YYYY-MM-DD");
  const days: StayCalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const day = gridStart.add(i, "day");
    const date = day.format("YYYY-MM-DD");
    const startMs = day.valueOf();
    const endMs = day.add(1, "day").valueOf();
    days.push({
      date,
      inMonth: day.year() === year && day.month() === monthIndex,
      isToday: date === today,
      events: events.filter((event) => {
        const window =
          event.kind === "lease"
            ? leaseWindow({ startDate: event.start, endDate: event.end })
            : stayWindow(event.start, event.end);
        if (!window) return false;
        return windowsOverlap(window, { startMs, endMs });
      }),
    });
  }
  return days;
}

export function stayMonthLabel(
  year: number,
  monthIndex: number,
  lang?: string,
  timeZone: string = STAY_DISPLAY_TIMEZONE,
): string {
  return new Intl.DateTimeFormat(stayDisplayLocale(lang), {
    month: "long",
    year: "numeric",
    timeZone,
  }).format(new Date(Date.UTC(year, monthIndex, 15)));
}

/** @deprecated Use stayMonthLabel with the UI language. */
export function utcMonthLabel(year: number, monthIndex: number): string {
  return stayMonthLabel(year, monthIndex, "en", "UTC");
}

export function shiftUtcMonth(
  year: number,
  monthIndex: number,
  delta: number,
): { year: number; monthIndex: number } {
  const next = monthIndex + delta;
  const yearDelta = Math.floor(next / 12);
  const month = ((next % 12) + 12) % 12;
  return { year: year + yearDelta, monthIndex: month };
}

export function nowInStayDisplayZone(timeZone: string = STAY_DISPLAY_TIMEZONE): Dayjs {
  return dayjs().tz(timeZone);
}

export function defaultStayDateTimes(
  timeZone: string = STAY_DISPLAY_TIMEZONE,
): { checkIn: Dayjs; checkOut: Dayjs } {
  const now = nowInStayDisplayZone(timeZone);
  let checkIn = now.hour(15).minute(0).second(0).millisecond(0);
  if (!checkIn.isAfter(now)) {
    checkIn = checkIn.add(1, "day");
  }
  const checkOut = checkIn.add(1, "day").hour(11).minute(0).second(0).millisecond(0);
  return { checkIn, checkOut };
}

/** Persist picker wall-clock as UTC ISO, treating digits as STAY_DISPLAY_TIMEZONE. */
export function toPersistedStayIso(
  value: Dayjs,
  timeZone: string = STAY_DISPLAY_TIMEZONE,
): string {
  return value.tz(timeZone, true).toISOString();
}

export function formatStayDateTime(
  iso: string,
  lang?: string,
  timeZone: string = STAY_DISPLAY_TIMEZONE,
): { local: string; utc: string } {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { local: iso, utc: iso };
  }
  const local = new Intl.DateTimeFormat(stayDisplayLocale(lang), {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(date);
  return { local, utc: local };
}

export function nextConfirmedArrival(
  stays: Array<{ status: string; checkIn: string; checkOut: string }>,
  nowMs: number = Date.now(),
): { checkIn: string; checkOut: string } | null {
  const upcoming = stays
    .filter((stay) => CONFIRMED_STAY_STATUSES.has(stay.status))
    .filter((stay) => {
      const end = parseUtcInstant(stay.checkOut);
      return end != null && end > nowMs;
    })
    .sort((a, b) => (parseUtcInstant(a.checkIn) ?? 0) - (parseUtcInstant(b.checkIn) ?? 0));
  return upcoming[0] ?? null;
}

export function currentStayOccupancy(
  stays: Array<{ status: string; checkIn: string; checkOut: string }>,
  nowMs: number = Date.now(),
): { checkIn: string; checkOut: string } | null {
  return (
    stays.find((stay) => {
      if (!CONFIRMED_STAY_STATUSES.has(stay.status)) return false;
      const window = stayWindow(stay.checkIn, stay.checkOut);
      return window != null && nowMs >= window.startMs && nowMs < window.endMs;
    }) ?? null
  );
}

export function blockingLeaseThrough(
  leases: Array<{
    status: string;
    startDate?: string | null;
    endDate?: string | null;
    occupancyEnd?: string | null;
  }>,
  nowMs: number = Date.now(),
): { end: string } | null {
  let latestEnd: number | null = null;
  let latestIso: string | null = null;
  for (const lease of leases) {
    if (!BLOCKING_LEASE_STATUSES.has(lease.status)) continue;
    const window = leaseWindow(lease);
    if (!window) continue;
    if (nowMs >= window.endMs) continue;
    if (latestEnd == null || window.endMs > latestEnd) {
      latestEnd = window.endMs;
      latestIso = lease.occupancyEnd?.trim() || lease.endDate?.trim() || null;
    }
  }
  return latestIso ? { end: latestIso } : null;
}
